import { Injectable, inject } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { FirestoreFieldExecutionProjectService } from './firestore-field-execution-project.service';
import { Project } from '../models/project.model';
import {
    applyResolvedGpNames,
    GpValidationResult,
    normalizeGpLookupKey,
    validateGpMasterForBlock,
} from '../utils/field-execution-gp-validation.utils';
import {
    buildProjectDuplicateKey,
    buildWithinSheetRowKey,
    DuplicateLookup,
    ProjectDuplicateIndex,
    subVillageFromImportRow,
} from '../utils/field-execution-import-duplicate.utils';
import { isNonImportableExcelRow } from '../utils/field-execution-import-parse.utils';
import { feAbstractGpProjectName } from '../utils/field-execution-stats.utils';

// ── Parsed row types ──────────────────────────────────────────────────────────

/** One GP row from an abstract (summary) Excel sheet */
export interface ParsedGpRow {
    gpName: string;
    projectCount: number;
    totalCostLakh: number;
}

/** One project row from a detailed Excel sheet */
export interface ParsedProjectRow {
    gpName: string;
    /** Village name read from the village column within the sheet (gp-per-sheet format) */
    villageName?: string;
    schemeName: string;
    projectName: string;
    estCostLakh: number;
    executantName: string;
    executantNumber: string;
    status: string;
    remarks: string;
}

export type SheetFormat = 'abstract' | 'detailed' | 'gp-per-sheet';

/** Preview data returned after parsing — before any Firestore write */
export interface SheetPreview {
    sheetName: string;
    blockName: string;
    /** GP name when format = 'gp-per-sheet' (= cleaned sheet tab name) */
    gpName?: string;
    /** Unique village names found within a gp-per-sheet sheet */
    villages?: string[];
    format: SheetFormat;
    gpRows: ParsedGpRow[];
    projectRows: ParsedProjectRow[];
    /** Total importable data rows */
    totalRows: number;
    parseErrors: string[];
}

export type DuplicateHandling = 'skip' | 'update' | 'allow';

export interface ImportActionPlan {
    totalRowsFound: number;
    validNewCount: number;
    duplicateCount: number;
    invalidCount: number;
    toCreate: number;
    toUpdate: number;
    toSkip: number;
    toReject: number;
    duplicateHandling: DuplicateHandling;
    canImport: boolean;
    gpValidationPassed: boolean;
    gpValidation: GpValidationResult | null;
    validationErrors: string[];
    duplicateSamples: string[];
    selectedActionLabel: string;
    buttonLabel: string;
    progressTotal: number;
    statusMessage?: string;
}

export interface ImportResult {
    imported: number;
    updated: number;
    skipped: number;
    rejected: number;
    validationErrors: string[];
    /** Human-readable label for each skipped row (GP / project name + block) */
    skippedItems: string[];
    /** Full project data for skipped rows so they can be added on demand */
    skippedProjects: Omit<Project, 'id'>[];
    errors: string[];
}

interface ImportRowKey {
    key: string;
    label: string;
    lookup: DuplicateLookup;
}

const DUPLICATE_ACTION_LABELS: Record<DuplicateHandling, string> = {
    skip:   'Skip duplicates (import new only)',
    update: 'Update existing',
    allow:  'Allow duplicates (import all)',
};

// ── Service ───────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class FieldExecutionImportService {
    private readonly fsService = inject(FirestoreFieldExecutionProjectService);

    // ── Parse Excel file ──────────────────────────────────────────────────────

    async parseFile(file: File): Promise<SheetPreview[]> {
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        // Exclude generic placeholder sheet names (Sheet1, Sheet2, Sheet13, etc.)
        const genericSheet = /^sheet\d+$/i;
        const all = wb.SheetNames
            .filter(name => !genericSheet.test(name.trim()))
            .map(name => this.parseSheet(wb.Sheets[name], name))
            .filter(s => s.totalRows > 0);
        // If the file uses GP-per-sheet format, return only those sheets.
        // This automatically excludes summary sheets like "FINAL NS PRO.".
        const gpSheets = all.filter(s => s.format === 'gp-per-sheet');
        return gpSheets.length > 0 ? gpSheets : all;
    }

    private parseSheet(ws: XLSX.WorkSheet, sheetName: string): SheetPreview {
        const rawRows: unknown[][] = XLSX.utils.sheet_to_json(ws, {
            header: 1, defval: '', raw: false
        });

        const preview: SheetPreview = {
            sheetName,
            blockName: this.extractBlockName(rawRows, sheetName),
            format: 'abstract',
            gpRows: [],
            projectRows: [],
            totalRows: 0,
            parseErrors: [],
        };

        // Find the first header row (scan up to 15 rows)
        let headerIdx = -1;
        let colMap: Record<string, number> = {};

        for (let i = 0; i < Math.min(15, rawRows.length); i++) {
            const cells = rawRows[i].map(c => String(c).toLowerCase().trim());
            colMap = this.detectColumns(cells);
            // Accept row as header if it has a GP column, a village column,
            // OR at least a project-name column (gp-per-sheet fallback)
            if (colMap['gp'] >= 0 || colMap['villageCol'] >= 0 || colMap['projectName'] >= 0) {
                headerIdx = i;
                break;
            }
        }

        if (headerIdx < 0) {
            preview.parseErrors.push('Could not detect column headers.');
            return preview;
        }

        // Detect format:
        // - gp-per-sheet: sheet tab = GP (no real GP column, just project rows per village)
        // - abstract: has GP column, no project-name column
        // - detailed: has both GP column and project-name column
        //
        // Conflict guard: if 'gp' and 'villageCol' resolved to the SAME column index
        // the header is something like "VILLAGE NAME" which belongs to village-level
        // data — treat the sheet as gp-per-sheet regardless.
        const gpVillageConflict =
            colMap['gp'] >= 0 &&
            colMap['villageCol'] >= 0 &&
            colMap['gp'] === colMap['villageCol'];

        if (colMap['gp'] >= 0 && !gpVillageConflict) {
            preview.format = colMap['projectName'] >= 0 ? 'detailed' : 'abstract';
        } else {
            // No real GP column (only villageCol, or gp=villageCol conflict) — gp-per-sheet
            preview.format    = 'gp-per-sheet';
            preview.blockName = '';
            // Extract GP name from first row: "NOT STARTED PROJECTS OF PADHANI GP" → "Padhani"
            preview.gpName    = this.extractGpNameFromHeader(rawRows[0]?.[0], sheetName);
        }

        // Parse data rows — gp-per-sheet reads actual village per row (loc), GP name used for grouping (village)
        // and skips exact duplicates (same name+scheme+cost)
        const gpSeenKeys = new Set<string>();

        for (let i = headerIdx + 1; i < rawRows.length; i++) {
            const row = rawRows[i];

            if (preview.format === 'gp-per-sheet') {
                const projName   = String(row[colMap['projectName']] ?? '').trim();
                const schemeName = String(row[colMap['scheme']]      ?? '').trim();
                const villageRaw = colMap['villageCol'] >= 0
                    ? String(row[colMap['villageCol']] ?? '').trim()
                    : '';
                if (isNonImportableExcelRow(projName, schemeName, villageRaw)) continue;
                // Skip repeated rows: same village + same project name (cost/scheme may differ)
                const dupKey = buildWithinSheetRowKey(
                    subVillageFromImportRow(preview.gpName!, villageRaw),
                    projName
                );
                if (gpSeenKeys.has(dupKey)) continue;
                gpSeenKeys.add(dupKey);
                preview.projectRows.push(this.parseDetailedRow(row, colMap, preview.gpName!));
            } else {                const gpRaw = String(row[colMap['gp']] ?? '').trim();
                // Skip sub-headers, empty rows, total rows
                if (!gpRaw || /^(total|name|signature|sl\.?\s*no)$/i.test(gpRaw)) continue;

                if (preview.format === 'abstract') {
                    preview.gpRows.push(this.parseAbstractRow(row, colMap, gpRaw));
                } else {
                    const projName = String(row[colMap['projectName']] ?? '').trim();
                    const schemeName = String(row[colMap['scheme']] ?? '').trim();
                    const villageRaw = colMap['villageCol'] >= 0
                        ? String(row[colMap['villageCol']] ?? '').trim()
                        : '';
                    if (isNonImportableExcelRow(projName, schemeName, villageRaw)) continue;
                    if (!projName) {
                        preview.parseErrors.push(`Row ${i + 1}: Missing project name.`);
                        continue;
                    }
                    preview.projectRows.push(this.parseDetailedRow(row, colMap, gpRaw));
                }
            }
        }

        preview.totalRows = preview.format === 'abstract'
            ? preview.gpRows.length
            : preview.projectRows.length;

        // Collect unique village names for gp-per-sheet preview display
        if (preview.format === 'gp-per-sheet') {
            const uniqueVillages = [
                ...new Set(
                    preview.projectRows
                        .map(r => r.villageName)
                        .filter((v): v is string => !!v)
                )
            ];
            if (uniqueVillages.length > 0) preview.villages = uniqueVillages;
        }

        return preview;
    }

    // ── Pre-import analysis (Steps 1–4) ──────────────────────────────────────

    /**
     * Classify rows, apply duplicate-handling choice, and return exact create/update/skip counts.
     * GP validation always runs first — duplicate mode never bypasses it.
     */
    async computeImportPlan(
        sheets: SheetPreview[],
        options: { gpBlock?: string; duplicateHandling: DuplicateHandling }
    ): Promise<ImportActionPlan> {
        const totalRowsFound = sheets.reduce((s, sh) => s + sh.totalRows, 0);
        const { duplicateHandling } = options;

        const plan: ImportActionPlan = {
            totalRowsFound,
            validNewCount: 0,
            duplicateCount: 0,
            invalidCount: 0,
            toCreate: 0,
            toUpdate: 0,
            toSkip: 0,
            toReject: 0,
            duplicateHandling,
            canImport: false,
            gpValidationPassed: false,
            gpValidation: null,
            validationErrors: [],
            duplicateSamples: [],
            selectedActionLabel: DUPLICATE_ACTION_LABELS[duplicateHandling],
            buttonLabel: 'Import',
            progressTotal: 0,
        };

        const isGpPerSheet = sheets.some(s => s.format === 'gp-per-sheet');
        if (isGpPerSheet && !options.gpBlock?.trim()) {
            plan.statusMessage = 'Select a block to validate GP names.';
            return plan;
        }

        const block = this.resolveImportBlock(sheets, options.gpBlock);
        const gpValidation = validateGpMasterForBlock(sheets, block);
        plan.gpValidation = gpValidation;

        if (!gpValidation.valid) {
            plan.invalidCount = totalRowsFound;
            plan.validationErrors = gpValidation.issues.map(issue => {
                const hint = issue.suggestion ? ` ${issue.suggestion}` : '';
                return `Sheet "${issue.sheetName}" — ${issue.message}${hint}`;
            });
            plan.statusMessage = 'Import blocked — resolve all GP validation errors before continuing.';
            return plan;
        }

        plan.gpValidationPassed = true;

        const existingProjects = await this.loadExistingFeProjects();
        const duplicateIndex = ProjectDuplicateIndex.fromProjects(existingProjects);

        const rows = this.collectImportRowKeys(sheets, block, gpValidation.resolvedNames);
        const duplicateSamples: string[] = [];
        const seenInFile = new Set<string>();
        let validNew = 0;
        let duplicate = 0;

        for (const row of rows) {
            if (duplicateHandling === 'allow') {
                validNew++;
                continue;
            }
            const isDup = duplicateIndex.has(row.lookup, duplicateHandling)
                || seenInFile.has(row.key);
            if (isDup) {
                duplicate++;
                if (duplicateSamples.length < 8) duplicateSamples.push(row.label);
            } else {
                validNew++;
                seenInFile.add(row.key);
            }
        }

        plan.validNewCount = validNew;
        plan.duplicateCount = duplicate;
        plan.duplicateSamples = duplicateSamples;
        plan.invalidCount = 0;

        this.applyDuplicateHandlingToPlan(plan, duplicateHandling);
        return plan;
    }

    private applyDuplicateHandlingToPlan(plan: ImportActionPlan, mode: DuplicateHandling): void {
        const { validNewCount: n, duplicateCount: d, totalRowsFound: total } = plan;

        switch (mode) {
            case 'skip':
                plan.toCreate = n;
                plan.toUpdate = 0;
                plan.toSkip = d;
                plan.progressTotal = n;
                plan.buttonLabel = n === 1 ? 'Import 1 Project' : `Import ${n} Projects`;
                if (n === 0 && d > 0) {
                    plan.canImport = false;
                    plan.statusMessage = 'No new projects available to import. All records already exist.';
                } else if (n > 0 && d > 0) {
                    plan.canImport = true;
                    plan.statusMessage =
                        `${n} new project(s) will be imported. ${d} duplicate(s) will be skipped.`;
                } else {
                    plan.canImport = n > 0;
                }
                break;

            case 'update':
                plan.toCreate = n;
                plan.toUpdate = d;
                plan.toSkip = 0;
                plan.progressTotal = n + d;
                plan.buttonLabel = `Create ${n} + Update ${d} Projects`;
                plan.canImport = n + d > 0;
                if (!plan.canImport) {
                    plan.statusMessage = 'No projects available to create or update.';
                } else if (n > 0 && d > 0) {
                    plan.statusMessage =
                        `${d} existing project(s) will be updated from Excel (cost, name, and other fields). ` +
                        `${n} new project(s) will be added.`;
                } else if (d > 0) {
                    plan.statusMessage =
                        `${d} project(s) will be updated from Excel (cost, name, and other fields).`;
                }
                break;

            case 'allow':
                plan.toCreate = total;
                plan.toUpdate = 0;
                plan.toSkip = 0;
                plan.progressTotal = total;
                plan.buttonLabel = total === 1 ? 'Import 1 Project' : `Import ${total} Projects`;
                plan.canImport = total > 0;
                break;
        }
    }

    private resolveImportBlock(sheets: SheetPreview[], gpBlock?: string): string {
        const isGpPerSheet = sheets.some(s => s.format === 'gp-per-sheet');
        if (isGpPerSheet) return gpBlock?.trim() || '';
        return sheets[0]?.blockName?.trim() || gpBlock?.trim() || '';
    }

    private resolveGpForDuplicateKey(
        gpNameRaw: string,
        resolvedNames: Map<string, string>
    ): string {
        return resolvedNames.get(normalizeGpLookupKey(gpNameRaw)) ?? gpNameRaw;
    }

    private collectImportRowKeys(
        sheets: SheetPreview[],
        block: string,
        resolvedNames: Map<string, string>
    ): ImportRowKey[] {
        const rows: ImportRowKey[] = [];

        for (const sheet of sheets) {
            const sheetBlock = sheet.format === 'gp-per-sheet' ? block : sheet.blockName;

            if (sheet.format === 'abstract') {
                for (const row of sheet.gpRows) {
                    const gp = this.resolveGpForDuplicateKey(row.gpName, resolvedNames);
                    const name = feAbstractGpProjectName(gp);
                    rows.push({
                        key: buildProjectDuplicateKey(sheetBlock, gp, gp, name),
                        label: `${sheetBlock} › ${gp}`,
                        lookup: { block: sheetBlock, gp, subVillage: gp, projectName: name },
                    });
                }
            } else {
                for (const row of sheet.projectRows) {
                    const gp = this.resolveGpForDuplicateKey(row.gpName, resolvedNames);
                    const sub = subVillageFromImportRow(gp, row.villageName);
                    rows.push({
                        key: buildProjectDuplicateKey(sheetBlock, gp, sub, row.projectName),
                        label: `${sheetBlock} › ${gp} › ${sub} › ${row.projectName}`,
                        lookup: {
                            block: sheetBlock,
                            gp,
                            subVillage: sub,
                            projectName: row.projectName,
                            scheme: row.schemeName,
                        },
                    });
                }
            }
        }

        return rows;
    }

    // ── Import data to Firestore ──────────────────────────────────────────────

    async importSheets(
        sheets: SheetPreview[],
        options: { duplicateHandling: DuplicateHandling; gpBlock?: string },
        onProgress: (done: number, total: number) => void
    ): Promise<ImportResult> {
        const result: ImportResult = {
            imported: 0, updated: 0, skipped: 0, rejected: 0,
            validationErrors: [], skippedItems: [], skippedProjects: [], errors: [],
        };

        const plan = await this.computeImportPlan(sheets, {
            gpBlock: options.gpBlock,
            duplicateHandling: options.duplicateHandling,
        });

        if (!plan.gpValidationPassed) {
            result.validationErrors = plan.validationErrors;
            result.errors.push(
                ...plan.validationErrors,
                'Import stopped: GP validation failed. Duplicate handling cannot bypass master-data checks.'
            );
            return result;
        }

        if (!plan.canImport) {
            if (plan.statusMessage) result.errors.push(plan.statusMessage);
            return result;
        }

        const block = this.resolveImportBlock(sheets, options.gpBlock);
        applyResolvedGpNames(sheets, block, plan.gpValidation!.resolvedNames);

        // Fetch existing projects for duplicate check (skip & update modes)
        let duplicateIndex = ProjectDuplicateIndex.fromProjects([]);
        if (options.duplicateHandling !== 'allow') {
            const existing = await this.loadExistingFeProjects();
            duplicateIndex = ProjectDuplicateIndex.fromProjects(existing);
        }

        const total = plan.progressTotal;
        let done = 0;

        for (const sheet of sheets) {
            if (sheet.format === 'abstract') {
                for (const row of sheet.gpRows) {
                    const projectName = feAbstractGpProjectName(row.gpName);
                    const key = buildProjectDuplicateKey(sheet.blockName, row.gpName, row.gpName, projectName);
                    const mode = options.duplicateHandling;
                    const existing = duplicateIndex.find({
                        block: sheet.blockName,
                        gp: row.gpName,
                        subVillage: row.gpName,
                        projectName,
                    }, mode);

                    if (existing && mode === 'skip') {
                        result.skipped++;
                        result.skippedItems.push(`${sheet.blockName} › ${row.gpName}`);
                        result.skippedProjects.push(this.buildAbstractProject(sheet.blockName, row, projectName));
                    } else if (existing && mode === 'update') {
                        try {
                            const payload = this.buildAbstractProject(sheet.blockName, row, projectName);
                            const updates = {
                                ...payload,
                                cost: payload.cost,
                                spent: existing.spent ?? 0,
                                physical: existing.physical ?? 0,
                            };
                            await this.fsService.updateProject(existing.id, updates);
                            duplicateIndex.registerAfterWrite({ ...existing, ...updates });
                            result.updated++;
                        } catch (e: unknown) {
                            result.rejected++;
                            result.errors.push(`${row.gpName}: ${this.formatImportError(e)}`);
                        }
                    } else {
                        try {
                            await this.fsService.createProject(
                                this.buildAbstractProject(sheet.blockName, row, projectName)
                            );
                            duplicateIndex.registerKey(key, { id: '_new' } as Project);
                            result.imported++;
                        } catch (e: unknown) {
                            result.rejected++;
                            result.errors.push(`${row.gpName}: ${this.formatImportError(e)}`);
                        }
                    }

                    if (mode !== 'skip' || !existing) {
                        done++;
                        onProgress(done, total);
                    }
                }
            } else {
                // 'detailed' or 'gp-per-sheet' — both use projectRows
                const blockName = sheet.format === 'gp-per-sheet'
                    ? (options.gpBlock || 'Unknown')
                    : sheet.blockName;

                for (const row of sheet.projectRows) {
                    const mode = options.duplicateHandling;
                    const sub = subVillageFromImportRow(row.gpName, row.villageName);
                    const key = buildProjectDuplicateKey(blockName, row.gpName, sub, row.projectName);
                    const existing = duplicateIndex.find({
                        block: blockName,
                        gp: row.gpName,
                        subVillage: sub,
                        projectName: row.projectName,
                        scheme: row.schemeName,
                    }, mode);

                    if (existing && mode === 'skip') {
                        result.skipped++;
                        result.skippedItems.push(`${blockName} › ${row.gpName} › ${sub} › ${row.projectName}`);
                        result.skippedProjects.push(this.buildDetailedProject(blockName, row));
                    } else if (existing && mode === 'update') {
                        try {
                            const updates = this.buildDetailedProjectUpdate(blockName, row, existing);
                            await this.fsService.updateProject(existing.id, updates);
                            duplicateIndex.registerAfterWrite({ ...existing, ...updates });
                            await this.clearAbstractGpPlaceholder(blockName, row.gpName, duplicateIndex);
                            result.updated++;
                        } catch (e: unknown) {
                            result.rejected++;
                            result.errors.push(`${row.projectName}: ${this.formatImportError(e)}`);
                        }
                    } else {
                        try {
                            await this.fsService.createProject(
                                this.buildDetailedProject(blockName, row)
                            );
                            duplicateIndex.registerKey(key, { id: '_new' } as Project);
                            await this.clearAbstractGpPlaceholder(blockName, row.gpName, duplicateIndex);
                            result.imported++;
                        } catch (e: unknown) {
                            result.rejected++;
                            result.errors.push(`${row.projectName}: ${this.formatImportError(e)}`);
                        }
                    }

                    if (mode !== 'skip' || !existing) {
                        done++;
                        onProgress(done, total);
                    }
                }
            }
        }

        return result;
    }

    /** Remove abstract GP summary row once real project lines exist for that GP. */
    private async clearAbstractGpPlaceholder(
        blockName: string,
        gpName: string,
        duplicateIndex: ProjectDuplicateIndex,
    ): Promise<void> {
        const projectName = feAbstractGpProjectName(gpName);
        const placeholder = duplicateIndex.find({
            block: blockName,
            gp: gpName,
            subVillage: gpName,
            projectName,
        }, 'update');
        if (!placeholder?.id || placeholder.id === '_new') return;
        try {
            await this.fsService.deleteProject(placeholder.id);
        } catch {
            /* non-fatal — rollup logic still excludes abstract rows when detailed exist */
        }
    }

    private formatImportError(e: unknown): string {
        const msg = e instanceof Error ? e.message : 'Write failed';
        if (/permission|unauthorized|insufficient/i.test(msg)) {
            return `${msg} — deploy Firestore rules: firebase deploy --only firestore:rules`;
        }
        return msg;
    }

    /** Load existing FE projects; empty list if Firestore is unavailable (e.g. rules not deployed). */
    private loadExistingFeProjects(): Promise<Project[]> {
        return firstValueFrom(
            this.fsService.getProjects$().pipe(
                catchError(err => {
                    console.warn('[FE import] Could not read field_execution_projects:', err);
                    return of([] as Project[]);
                })
            )
        );
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private extractBlockName(rows: unknown[][], fallback: string): string {
        const title = String(rows[0]?.[0] ?? '');
        // e.g. " Tihidi GP Wise N/S Abstract..." → "Tihidi"
        const match = title.match(/^\s*([A-Za-z]+)\s+(?:GP|Block)/i);
        if (match) return this.toTitleCase(match[1]);
        return fallback;
    }

    /** Extract GP name from first-row header like "NOT STARTED PROJECTS OF PADHANI GP" → "Padhani" */
    private extractGpNameFromHeader(firstCell: unknown, fallback: string): string {
        const m = String(firstCell ?? '').match(/\bOF\s+(.+?)\s+GP\b/i);
        if (m) {
            const raw = m[1].trim().replace(/[^a-zA-Z]+$/g, '').trim();
            return this.toTitleCase(raw || m[1].trim());
        }
        return this.toTitleCase(fallback.trim());
    }

    /**
     * Map lowercase header cell strings to column indices.
     * Returns -1 for columns not found.
     */
    private detectColumns(cells: string[]): Record<string, number> {
        const find = (...kw: string[]): number =>
            cells.findIndex(c => kw.some(k => c.includes(k)));

        return {
            gp:           find('name of the gp', 'gp name', 'name of gp', 'village name', 'name of village/gp'),
            villageCol:   find('name of the village', 'name of village', 'village name', 'name of the gp/village',
                               // bare "village" column (e.g. HASNABAD sheet)
                               'village'),
            count:        find('no. of non', 'no of non', 'project count', 'count'),
            cost:         find('estmate cost', 'estimated cost', 'estimate cost', 'estt.cost', 'est. cost',
                               'total cost', 'est cost', 'cost'),
            projectName:  find('project name', 'name of project', 'name of the project', 'work name', 'project/work name',
                               // handles typo "NAMEW OF PROJECT" in some sheets
                               'ame of project', 'of project'),
            scheme:       find('scheme', 'name of scheme', 'scheme name'),
            executant:    find('executant name', 'name of executant', 'executor name', 'executing agency',
                               'name of executant', 'executant', 'agency name', 'contractor name',
                               'implementing agency', 'agency', 'contractor', 'self help group', 'shg'),
            executantNum: find('executant number', 'executor number', 'contact no', 'contact number',
                               'contact', 'mobile no', 'mobile number', 'mobile', 'phone no', 'phone number',
                               'phone', 'mob no', 'mob'),
            sanctionYear:  find('sanctioned year', 'sanction year', 'sanctioned year', 'sanction year'),
            status:       find('work status', 'status'),
            remarks:      find('remarks', 'remark'),
        };
    }

    private parseAbstractRow(
        row: unknown[], colMap: Record<string, number>, gpName: string
    ): ParsedGpRow {
        const rawCost = parseFloat(String(row[colMap['cost']] ?? '0').replace(/,/g, '')) || 0;
        // Auto-detect unit: if value looks like rupees (> 10,000), convert to lakhs
        const totalCostLakh = rawCost > 10000 ? rawCost / 100000 : rawCost;

        return {
            gpName: this.toTitleCase(gpName),
            projectCount: parseInt(String(row[colMap['count']] ?? '0'), 10) || 0,
            totalCostLakh,
        };
    }

    private parseCostLakh(row: unknown[], colMap: Record<string, number>): number {
        const read = (idx: number): number => {
            if (idx < 0) return 0;
            const raw = parseFloat(String(row[idx] ?? '0').replace(/,/g, ''));
            return Number.isFinite(raw) ? raw : 0;
        };

        let rawCost = read(colMap['cost']);
        if (rawCost <= 0) {
            for (let i = 0; i < row.length; i++) {
                const cell = String(row[i] ?? '').trim();
                if (!cell || /[a-z]/i.test(cell)) continue;
                const n = parseFloat(cell.replace(/,/g, ''));
                if (Number.isFinite(n) && n > 0 && n < 1_000_000) {
                    rawCost = n;
                    break;
                }
            }
        }
        return rawCost > 10000 ? rawCost / 100000 : rawCost;
    }

    private parseDetailedRow(
        row: unknown[], colMap: Record<string, number>, gpName: string
    ): ParsedProjectRow {
        const estCostLakh = this.parseCostLakh(row, colMap);

        const villageRaw = colMap['villageCol'] >= 0
            ? String(row[colMap['villageCol']] ?? '').trim()
            : '';

        return {
            gpName: this.toTitleCase(gpName),
            villageName: villageRaw ? this.toTitleCase(villageRaw) : undefined,
            schemeName: String(row[colMap['scheme']] ?? '').trim(),
            projectName: String(row[colMap['projectName']] ?? '').trim(),
            estCostLakh,
            executantName: String(row[colMap['executant']] ?? '').trim(),
            executantNumber: String(row[colMap['executantNum']] ?? '').trim(),
            status: this.normalizeImportStatus(String(row[colMap['status']] ?? '').trim()),
            remarks: String(row[colMap['remarks']] ?? '').trim(),
        };
    }

    private buildAbstractProject(
        blockName: string, row: ParsedGpRow, name: string
    ): Omit<Project, 'id'> {
        return {
            name,
            dept: '',
            gp: row.gpName,
            loc: row.gpName,
            block: blockName,
            village: row.gpName,
            projectScope: 'field_execution' as const,
            cost: row.totalCostLakh,
            spent: 0,
            physical: 0,
            status: 'Planned',
            start: '',
            end: '',
            priority: false,
            remarks: `${row.projectCount} non-started project(s) (abstract import)`,
        };
    }

    private normalizeImportStatus(raw: string): Project['status'] {
        const validStatuses = ['Completed', 'In Progress', 'Stuck', 'Planned'] as const;
        if ((validStatuses as readonly string[]).includes(raw)) return raw as Project['status'];
        if (/^(n\/s|ns|not\s*started|planned)$/i.test(raw)) return 'Planned';
        return 'Planned';
    }

    private buildDetailedProject(
        blockName: string, row: ParsedProjectRow
    ): Omit<Project, 'id'> {
        return {
            name: row.projectName,
            dept: '',
            gp: row.gpName,
            loc: row.villageName || row.gpName,
            block: blockName,
            village: row.gpName,
            projectScope: 'field_execution' as const,
            scheme: row.schemeName || undefined,
            cost: row.estCostLakh,
            spent: 0,
            physical: 0,
            status: this.normalizeImportStatus(row.status),
            start: '',
            end: '',
            priority: false,
            executorName: row.executantName || undefined,
            executorNumber: row.executantNumber || undefined,
            remarks: row.remarks,
        };
    }

    /** Merge Excel row onto an existing project — always applies cost from the file. */
    private buildDetailedProjectUpdate(
        blockName: string, row: ParsedProjectRow, existing: Project
    ): Partial<Omit<Project, 'id'>> {
        const fresh = this.buildDetailedProject(blockName, row);
        return {
            name: fresh.name,
            gp: fresh.gp,
            loc: fresh.loc,
            block: fresh.block,
            village: fresh.village,
            scheme: fresh.scheme,
            cost: fresh.cost,
            status: fresh.status,
            executorName: fresh.executorName,
            executorNumber: fresh.executorNumber,
            remarks: fresh.remarks || existing.remarks,
            spent: existing.spent ?? 0,
            physical: existing.physical ?? 0,
            start: existing.start ?? '',
            end: existing.end ?? '',
            priority: existing.priority ?? false,
        };
    }

    /** Import a set of already-built project objects (used for "Add Anyway" on skipped items). */
    async importSkipped(
        projects: Omit<Project, 'id'>[],
        onProgress: (done: number, total: number) => void
    ): Promise<number> {
        let imported = 0;
        for (const p of projects) {
            const block = p.block ?? '';
            const gpValidation = validateGpMasterForBlock(
                [{
                    sheetName: p.village ?? '',
                    blockName: block,
                    gpName: p.village,
                    format: 'gp-per-sheet',
                    gpRows: [],
                    projectRows: [{ gpName: p.village ?? '' }],
                }],
                block
            );
            if (!gpValidation.valid) continue;

            const canonical = gpValidation.resolvedNames.get(normalizeGpLookupKey(p.village ?? ''));
            const payload = canonical
                ? { ...p, village: canonical, loc: p.loc === p.village ? canonical : p.loc }
                : p;

            try { await this.fsService.createProject(payload); imported++; } catch { /* ignore */ }
            onProgress(imported, projects.length);
        }
        return imported;
    }

    private toTitleCase(s: string): string {
        return s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    }

    // ── Import Template Download ───────────────────────────────────────────────

    /**
     * Generates and downloads an Excel import template (.xlsx).
     *
     * Structure (mirrors the existing import file format):
     *   • One sheet per sample GP (sheet tab = GP name)
     *   • Row 0: "PROJECTS OF [GP_NAME] GP"  (title — used by parser to extract GP name)
     *   • Row 1: Column headers (exact names the import service detects)
     *   • Row 2-4: Sample data rows
     *   • Final sheet: "Instructions"
     */
    downloadTemplate(): void {
        const wb = XLSX.utils.book_new();

        const HEADERS = [
            'Sl. No',
            { wch: 16 }, { wch: 14 }, { wch: 24 }, { wch: 14 }, { wch: 30 },
        ];

        // ── Sample GPs ────────────────────────────────────────────────────────
        const sampleGPs: Array<{ tab: string; display: string; scheme: string }> = [
            { tab: 'BILANA',   display: 'Bilana',   scheme: '5th SFC (24-25)' },
            { tab: 'KANPADA',  display: 'Kanpada',  scheme: 'CMSA (24-25)'    },
            { tab: 'ACHAK',    display: 'Achak',    scheme: '15th FC (24-25)' },
        ];

        for (const gp of sampleGPs) {
            const wsData: unknown[][] = [
                // Row 0 — title row (parser reads this to extract GP name)
                [`PROJECTS OF ${gp.tab} GP`],
                // Row 1 — column headers
                HEADERS,
                // Row 2-4 — sample rows
                [
                    1, gp.display, gp.scheme,
                    `Maintenance of market complex at ${gp.display}`,
                    312500, 'N/S', 'Rajesh Kumar', '9876543210', 'Work pending',
                ],
                [
                    2, gp.display, 'CMSA (24-25)',
                    `Renovation of school building at ${gp.display}`,
                    300000, 'Planned', 'Amit Das', '9123456789', 'Pending approval',
                ],
                [
                    3, gp.display, '15th FC (24-25)',
                    `Construction of community hall at ${gp.display}`,
                    500000, 'In Progress', '', '', '',
                ],
            ];

            const ws = XLSX.utils.aoa_to_sheet(wsData);
            const COL_WIDTHS = [
            { wch: 4 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 28 }
        ];
            // Merge title cell A1 across all columns for visual clarity
            ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: HEADERS.length - 1 } }];
            XLSX.utils.book_append_sheet(wb, ws, gp.tab);
        }

        // ── Instructions sheet ────────────────────────────────────────────────
        const instrData: unknown[][] = [
            ['FIELD EXECUTION IMPORT TEMPLATE — INSTRUCTIONS'],
            [''],
            ['GENERAL RULES', ''],
            ['1.', 'One sheet = One GP (Gram Panchayat)'],
            ['2.', 'Sheet / tab name must be the GP name (e.g., BILANA, KANPADA, ACHAK)'],
            ['3.', 'Do NOT change or rename the column headers'],
            ['4.', 'Do NOT remove mandatory columns (NAME OF PROJECT, ESTMATE COST)'],
            ['5.', 'ESTMATE COST must be numeric only — enter rupees (e.g., 312500) or lakhs (e.g., 3.125)'],
            ['6.', 'Contact No. should be a valid 10-digit mobile number'],
            ['7.', 'No duplicate project names within the same GP sheet'],
            ['8.', 'GP sheet/tab names must exactly match the master GP list for the selected Block (import stops on invalid GPs)'],
            ['9.', 'Duplicate handling (Skip / Update / Allow) applies to projects only — it never creates new GPs or Blocks'],
            ['10.', 'All saved data is live — changes appear immediately in the app'],
            [''],
            ['ALLOWED STATUS VALUES', ''],
            ['N/S',         'Not Started — project has not yet begun (saved as "Planned")'],
            ['Planned',     'Project is planned but work not started'],
            ['In Progress', 'Work is currently ongoing'],
            ['Completed',   'Work is fully finished'],
            [''],
            ['COLUMN REFERENCE', ''],
            ['Sl. No',                'Serial number (optional, can be blank)'],
            ['Name of Village',       'Village name within the GP (used for sub-grouping)'],
            ['Name of Scheme',        'Scheme name, e.g. 5th SFC, CMSA, 15th FC'],
            ['NAME OF PROJECT',       '★ REQUIRED — Full descriptive project name'],
            ['ESTMATE COST',          '★ REQUIRED — Estimated cost (numeric, in Rupees or Lakhs)'],
            ['STATUS',                'Work status — see allowed values above'],
            ['Name of the Executant', 'Name of contractor / executing agency'],
            ['Contact No.',           'Mobile number of executant / contractor'],
            ['REMARKS',               'Any additional notes or comments'],
            [''],
            ['COMMON ERRORS', ''],
            ['Missing project name',    'Row will be skipped — NAME OF PROJECT is mandatory'],
            ['Non-numeric cost',        'Cost will be set to 0 — use numbers only'],
            ['Wrong status value',      'Unrecognised status defaults to "Planned"'],
            ['Duplicate project name',  'Second occurrence in the same sheet is skipped'],
            ['Sheet named "Sheet1" etc.', 'Generic sheet names are ignored — use a real GP name'],
        ];

        const instrWs = XLSX.utils.aoa_to_sheet(instrData);
        instrWs['!cols'] = [{ wch: 28 }, { wch: 62 }];
        XLSX.utils.book_append_sheet(wb, instrWs, 'Instructions');

        XLSX.writeFile(wb, 'field-execution-import-template.xlsx');
    }
}
